import { useCallback, useEffect, useRef, useState } from "react";

import { isUserRejection } from "../services/relay.service";
import {
  VerdictSignatureStatus,
  describeChainError,
  fetchVerdictSignatures,
  signAndSubmitVerdict,
  verdictEndpointUnavailable,
  verdictSignatureErrorCopy,
} from "../services/challenge.service";

// The server-collected lane for a panel determination (V10.1.2). The counterpart of
// usePanelVerdict, which collects signatures in the browser and submits recordOutcome itself —
// that one is now only the fallback for when this endpoint cannot be reached.
//
//   GET  /challenges/:id/verdict-signatures   polled while `active`, so every seat watches the
//                                             same tally fill and sees `seated` flip false once
//                                             the server's recordOutcome lands
//   POST /challenges/:id/verdict-signatures   one seat's signature on one verdict
//
// `unavailable` means the endpoint itself is down or missing (verdictEndpointUnavailable) — the
// caller switches to the paste flow. A refusal (400 / 403) is NOT unavailability: it is surfaced
// as `error` and the lane stays up.

// The worker that sends recordOutcome ticks about once a minute; checking at a quarter of that is
// quick enough to feel live without hammering a public endpoint.
const POLL_MS = 15000;

export const useVerdictSignatures = (challengeId: bigint | null, active: boolean) => {
  const [status, setStatus] = useState<VerdictSignatureStatus | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The challenge a read belongs to, so a slow response for a previous challenge never lands on
  // the current one.
  const currentId = useRef(challengeId);
  currentId.current = challengeId;

  const refresh = useCallback(async () => {
    if (challengeId === null) {
      return;
    }
    const id = challengeId;
    try {
      const next = await fetchVerdictSignatures(id);
      if (mounted.current && currentId.current === id) {
        setStatus(next);
        setUnavailable(false);
      }
    } catch (e: any) {
      if (mounted.current && currentId.current === id && verdictEndpointUnavailable(e)) {
        setUnavailable(true);
      }
    }
  }, [challengeId]);

  // First read, then poll while active. A fresh challenge starts from a clean slate.
  useEffect(() => {
    setStatus(null);
    setUnavailable(false);
    setError(null);
    if (challengeId === null || !active) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    refresh().finally(() => {
      if (!cancelled && mounted.current) {
        setLoading(false);
      }
    });
    const timer = window.setInterval(() => {
      if (!document.hidden) {
        refresh();
      }
    }, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [challengeId, active, refresh]);

  // One MetaMask typed-data prompt (no gas), then the POST. Resolves true once the server has
  // stored the signature; the tally it returns is folded in straight away rather than waiting for
  // the next poll.
  const sign = useCallback(
    async (upheld: boolean, inBreach: boolean): Promise<boolean> => {
      if (challengeId === null) {
        return false;
      }
      setError(null);
      setSigning(true);
      try {
        const verdicts = await signAndSubmitVerdict(challengeId, upheld, inBreach);
        if (mounted.current) {
          setStatus((prev) => (prev ? { ...prev, verdicts } : prev));
        }
        // The POST may have tipped a verdict over the threshold, and `seated` is the GET's to say.
        refresh();
        return true;
      } catch (e: any) {
        if (!mounted.current || isUserRejection(e)) {
          return false;
        }
        if (verdictEndpointUnavailable(e) && e?.response !== undefined) {
          setUnavailable(true);
        }
        setError(verdictSignatureErrorCopy(e) ?? describeChainError(e));
        return false;
      } finally {
        if (mounted.current) {
          setSigning(false);
        }
      }
    },
    [challengeId, refresh]
  );

  return { status, unavailable, loading, signing, error, sign, refresh };
};

export default useVerdictSignatures;
